import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head } from "./shared";
import { Loader2, RefreshCw, DollarSign, Repeat, XCircle, Trash2 } from "lucide-react";

const STATUS_STYLE = {
  active: "text-green-300 bg-green-500/10 border-green-500/20",
  pending: "text-amber-300 bg-amber-500/10 border-amber-500/20",
  canceled: "text-gray-400 bg-white/5 border-white/10",
  past_due: "text-red-300 bg-red-500/10 border-red-500/20",
};

export function Subscriptions() {
  const [data, setData] = useState({ subscriptions: [], mrr: 0, active_count: 0 });
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/subscriptions").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runBilling = async () => {
    setBilling(true);
    try {
      const { data } = await api.post("/admin/subscriptions/run-billing");
      toast.success(`Billing run complete — ${data.billed} invoice(s) generated.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBilling(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this subscription? No further invoices will be generated.")) return;
    try { await api.post(`/subscriptions/${id}/cancel`); toast.success("Subscription canceled."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this subscription record? This cannot be undone.")) return;
    try { await api.delete(`/admin/subscriptions/${id}`); toast.success("Subscription deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-subscriptions">
      <Head title="Plans & Subscriptions" desc="Recurring Hosting & Care revenue." />
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="bg-navy-800 border border-gold/20 p-5 min-w-[180px]">
          <div className="text-[11px] uppercase tracking-wider text-gold flex items-center gap-1.5 mb-1"><DollarSign size={12} /> Monthly recurring</div>
          <div className="font-heading text-3xl text-white">${data.mrr?.toLocaleString()}<span className="text-sm text-gray-500">/mo</span></div>
        </div>
        <div className="bg-navy-800 border border-white/10 p-5 min-w-[140px]">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 flex items-center gap-1.5 mb-1"><Repeat size={12} /> Active plans</div>
          <div className="font-heading text-3xl text-white">{data.active_count}</div>
        </div>
        <button onClick={runBilling} disabled={billing} data-testid="run-billing-btn"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-white/20 text-white hover:bg-white/5 disabled:opacity-50">
          {billing ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />} Run billing now
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : data.subscriptions.length === 0 ? (
        <div className="text-gray-500 text-sm bg-navy-800 border border-white/10 p-8 text-center" data-testid="subs-empty">No subscriptions yet. They'll appear here as clients subscribe to a plan.</div>
      ) : (
        <div className="bg-navy-800 border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="subs-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                <th className="p-3">Plan</th><th className="p-3">Customer</th><th className="p-3">Amount</th>
                <th className="p-3">Status</th><th className="p-3">Next bill</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.subscriptions.map((s) => (
                <tr key={s.id} className="border-b border-white/5" data-testid={`sub-row-${s.id}`}>
                  <td className="p-3 text-white">{s.plan_name}</td>
                  <td className="p-3 text-gray-300">{s.org_name || s.email}</td>
                  <td className="p-3 text-gray-300">${s.amount}/mo</td>
                  <td className="p-3"><span className={`text-[11px] px-2 py-0.5 border ${STATUS_STYLE[s.status] || STATUS_STYLE.canceled}`}>{s.status}</span></td>
                  <td className="p-3 text-gray-400">{s.next_billing_date || "—"}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      {s.status === "active" && (
                        <button onClick={() => cancel(s.id)} data-testid={`cancel-sub-${s.id}`} className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200"><XCircle size={12} /> Cancel</button>
                      )}
                      <button onClick={() => remove(s.id)} data-testid={`delete-sub-${s.id}`} className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"><Trash2 size={12} /> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
